import * as React from "react";
import { AlertColor, Autocomplete, Box, Paper, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import CclLoadingButton from "../../../../components/common/cclButtons/cclLoadingButton";
import CclDataGridToolbar from "../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import ComponentLoader from "../../../../components/common/componentLoader";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { useGetActiveResourcesQuery } from "../../../../services/cclTokenedEnterpriseCoachingApi";
import {
    useGetAllTrainersQuery,
    useGetPayDatesQuery,
    useLazyGetPayrollReportQuery,
    useLazyGetWhenPaidReportQuery,
} from "../../../../services/cclTokenedPayrollApi";
import {
    formatCurrency,
    formatDateOnly,
    getReportDefinition,
    parseDateOnly,
    PayrollReportKey,
    toDateOnlyString,
} from "./payrollReportDefinitions";

type PayPeriodOption = { value: string; label: string; payDate: Date };
type TrainerOption = { empId: number; label: string };

type ReportResult = {
    key: PayrollReportKey;
    subtitle: string;
    rows: any[];
};

// A pay period ends on the Saturday before its Friday pay date.
const PAY_PERIOD_END_OFFSET_DAYS = 6;

const dateInputSx = { flex: "1 1 140px", minWidth: 140 };

const resultsHeight = "calc(100vh - 520px)";

// Buttons run along the bottom of each card, starting on the left.
const buttonRowSx = { justifyContent: "flex-start", mt: "auto" };

const PayrollReports: React.FC = () => {
    const { data: payDates = [], isLoading: payDatesLoading } = useGetPayDatesQuery();
    const { data: trainers = [], isLoading: trainersLoading } = useGetAllTrainersQuery();
    const { data: resources } = useGetActiveResourcesQuery();
    const [fetchPayrollReport] = useLazyGetPayrollReportQuery();
    const [fetchWhenPaidReport] = useLazyGetWhenPaidReportQuery();
    const { logEvent } = useLogAccessEvent();

    const [payPeriodEnd, setPayPeriodEnd] = React.useState<PayPeriodOption | null>(null);
    const [trainer, setTrainer] = React.useState<TrainerOption | null>(null);
    const [fromDate, setFromDate] = React.useState<Date | null>(null);
    const [toDate, setToDate] = React.useState<Date | null>(null);
    const [runningReport, setRunningReport] = React.useState<PayrollReportKey | null>(null);
    const [result, setResult] = React.useState<ReportResult | null>(null);
    const [snackbar, setSnackbar] = React.useState<{ message: string; severity: AlertColor } | null>(null);

    const payPeriodOptions = React.useMemo<PayPeriodOption[]>(() => {
        return payDates
            .map((payDate) => parseDateOnly(payDate.payDays))
            .filter((date): date is Date => date != null)
            .sort((a, b) => b.getTime() - a.getTime())
            .map((payDate) => {
                const periodEnd = new Date(payDate);
                periodEnd.setDate(periodEnd.getDate() - PAY_PERIOD_END_OFFSET_DAYS);

                return {
                    value: toDateOnlyString(periodEnd),
                    label: `${formatDateOnly(periodEnd)} (Pay Date ${formatDateOnly(payDate)})`,
                    payDate,
                };
            });
    }, [payDates]);

    // Default to the pay period that will be paid next.
    React.useEffect(() => {
        if (payPeriodEnd != null || payPeriodOptions.length === 0) {
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = payPeriodOptions.filter((option) => option.payDate >= today);
        setPayPeriodEnd(upcoming.length > 0 ? upcoming[upcoming.length - 1] : payPeriodOptions[0]);
    }, [payPeriodOptions, payPeriodEnd]);

    const nameByEmpId = React.useMemo(() => {
        const nameByImKey = new Map<number, string>();
        (resources ?? []).forEach((resource: any) => {
            const name = [resource.lastname, resource.firstname].filter(Boolean).join(", ");
            if (resource.imkey != null && name) {
                nameByImKey.set(Number(resource.imkey), name);
            }
        });

        const names = new Map<number, string>();
        trainers.forEach((t: any) => {
            names.set(t.empId, nameByImKey.get(Number(t.imKey)) ?? `Employee ${t.empId}`);
        });
        return names;
    }, [resources, trainers]);

    const trainerOptions = React.useMemo<TrainerOption[]>(
        () =>
            [...nameByEmpId.entries()]
                .map(([empId, label]) => ({ empId, label }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [nameByEmpId]
    );

    const lookupTrainerName = React.useCallback(
        (empId: number | null | undefined) => (empId == null ? "" : nameByEmpId.get(empId) ?? `Employee ${empId}`),
        [nameByEmpId]
    );

    const definition = React.useMemo(
        () => (result ? getReportDefinition(result.key, lookupTrainerName) : null),
        [result, lookupTrainerName]
    );

    const showError = (message: string) => setSnackbar({ message, severity: "error" });

    const runPayrollReport = async (key: PayrollReportKey) => {
        if (!payPeriodEnd) {
            showError("Select a Pay Period End Date.");
            return;
        }

        setRunningReport(key);
        try {
            const rows = await fetchPayrollReport(payPeriodEnd.value, false).unwrap();
            const periodEnd = parseDateOnly(payPeriodEnd.value);
            // Subtract calendar days (not 24h blocks) so a daylight-saving change doesn't shift the displayed day.
            const periodStart = periodEnd ? new Date(periodEnd) : null;
            periodStart?.setDate(periodStart.getDate() - 13);
            setResult({
                key,
                subtitle: `Pay period ${formatDateOnly(periodStart)} - ${formatDateOnly(periodEnd)}, pay date ${formatDateOnly(
                    payPeriodEnd.payDate
                )}`,
                rows,
            });
            logEvent("PayrollReportRun", {
                reportName: getReportDefinition(key, lookupTrainerName).title,
            });
        } catch (error) {
            console.error("Error running payroll report:", error);
            showError("Failed to run the report.");
        } finally {
            setRunningReport(null);
        }
    };

    const runWhenPaidReport = async (key: PayrollReportKey, byPayDate: boolean) => {
        if (!trainer) {
            showError("Select a Faculty Member.");
            return;
        }
        if (!fromDate || !toDate || Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
            showError("Enter a valid From and To date.");
            return;
        }
        if (toDate < fromDate) {
            showError("The To date must not be earlier than the From date.");
            return;
        }

        setRunningReport(key);
        try {
            const rows = await fetchWhenPaidReport(
                { empId: trainer.empId, from: toDateOnlyString(fromDate), to: toDateOnlyString(toDate), byPayDate },
                false
            ).unwrap();
            setResult({
                key,
                subtitle: `${trainer.label}, ${
                    byPayDate ? "paid in pay periods" : "work done"
                } ${formatDateOnly(fromDate)} - ${formatDateOnly(toDate)}`,
                rows,
            });
            logEvent("PayrollReportRun", {
                reportName: getReportDefinition(key, lookupTrainerName).title,
            });
        } catch (error) {
            console.error("Error running when-was-I-paid report:", error);
            showError("Failed to run the report.");
        } finally {
            setRunningReport(null);
        }
    };

    // Sorted at display time so trainer names that finish loading after the report was run are still used.
    const sortedRows = React.useMemo(
        () => (result ? sortPayrollRows(result.key, result.rows, lookupTrainerName) : []),
        [result, lookupTrainerName]
    );

    const totals = React.useMemo(() => {
        if (!result || !definition) {
            return [];
        }
        return definition.totals.map((total) => {
            const sum = result.rows.reduce((acc, row) => acc + Number(row[total.field] ?? 0), 0);
            return { label: total.label, value: total.currency ? formatCurrency(sum) : sum.toLocaleString("en-US") };
        });
    }, [result, definition]);

    const buttonSx = { minWidth: 0 };
    const isRunning = runningReport != null;

    return (
        <Box sx={{ width: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <CclAlertSnackbar
                open={snackbar != null}
                message={snackbar?.message ?? ""}
                severity={snackbar?.severity}
                onClose={() => setSnackbar(null)}
            />
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch" sx={{ width: 1 }}>
                <Paper
                    variant="outlined"
                    sx={{ p: 2.5, flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
                            Payroll Report
                        </Typography>
                        <Autocomplete
                            size="small"
                            options={payPeriodOptions}
                            loading={payDatesLoading}
                            value={payPeriodEnd}
                            onChange={(_event, option) => setPayPeriodEnd(option)}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                                <TextField {...params} label="Pay Period End Date" />
                            )}
                        />
                    </Box>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={buttonRowSx}>
                        {(
                            [
                                ["review", "Review Payroll Report"],
                                ["real", "Real Payroll Report"],
                                ["reconcile", "Reconcile Payroll Report"],
                            ] as [PayrollReportKey, string][]
                        ).map(([key, label]) => (
                            <CclLoadingButton
                                key={key}
                                mode="primary"
                                size="small"
                                sx={buttonSx}
                                disabled={isRunning && runningReport !== key}
                                loading={runningReport === key}
                                onClick={() => runPayrollReport(key)}
                            >
                                {label}
                            </CclLoadingButton>
                        ))}
                    </Stack>
                </Paper>
                <Paper
                    variant="outlined"
                    sx={{ p: 2.5, flex: "1.6 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
                            &quot;When Was I Paid?&quot; Report
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                            <Autocomplete
                                size="small"
                                sx={{ flex: "2 1 200px", minWidth: 200 }}
                                options={trainerOptions}
                                loading={trainersLoading}
                                value={trainer}
                                onChange={(_event, option) => setTrainer(option)}
                                isOptionEqualToValue={(a, b) => a.empId === b.empId}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => (
                                    <TextField {...params} label="Faculty Member" />
                                )}
                            />
                            <DatePicker
                                label="From"
                                value={fromDate}
                                onChange={(date: Date | null) => setFromDate(date)}
                                renderInput={(params: TextFieldProps) => (
                                    <TextField {...params} size="small" sx={dateInputSx} />
                                )}
                            />
                            <DatePicker
                                label="To"
                                value={toDate}
                                onChange={(date: Date | null) => setToDate(date)}
                                renderInput={(params: TextFieldProps) => (
                                    <TextField {...params} size="small" sx={dateInputSx} />
                                )}
                            />
                        </Stack>
                    </Box>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={buttonRowSx}>
                        <CclLoadingButton
                            mode="primary"
                            size="small"
                            sx={buttonSx}
                            disabled={isRunning && runningReport !== "whenPaidByWorkDate"}
                            loading={runningReport === "whenPaidByWorkDate"}
                            onClick={() => runWhenPaidReport("whenPaidByWorkDate", false)}
                        >
                            By Date Work Done
                        </CclLoadingButton>
                        <CclLoadingButton
                            mode="primary"
                            size="small"
                            sx={buttonSx}
                            disabled={isRunning && runningReport !== "whenPaidByPayPeriod"}
                            loading={runningReport === "whenPaidByPayPeriod"}
                            onClick={() => runWhenPaidReport("whenPaidByPayPeriod", true)}
                        >
                            By Date of Pay Period
                        </CclLoadingButton>
                    </Stack>
                </Paper>
            </Stack>
            {result && definition ? (
                <Box sx={{ display: "flex", flexDirection: "column", width: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        {definition.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {result.subtitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {`${result.rows.length.toLocaleString("en-US")} rows`}
                        {totals.map((total) => ` | ${total.label}: ${total.value}`).join("")}
                    </Typography>
                    <Box sx={{ position: "relative", height: resultsHeight, minHeight: 400 }}>
                        <DataGridPremium
                            // Remount so column state (widths/sort) doesn't leak between different reports.
                            key={result.key}
                            columns={definition.columns}
                            rows={sortedRows}
                            getRowId={(row) => row.paymentId}
                            density="compact"
                            experimentalFeatures={{ columnGrouping: true }}
                            columnGroupingModel={definition.columnGroupingModel}
                            disableSelectionOnClick
                            components={{ Toolbar: CclDataGridToolbar }}
                            componentsProps={{
                                toolbar: {
                                    showSaveState: false,
                                    hideSettingsButtons: true,
                                    savestate: null,
                                    clearstate: null,
                                    anySelected: false,
                                },
                            }}
                        />
                        {isRunning && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 1,
                                    bgcolor: "background.paper",
                                    opacity: 0.85,
                                }}
                            >
                                <ComponentLoader msg={"Loading"} />
                            </Box>
                        )}
                    </Box>
                </Box>
            ) : (
                isRunning && (
                    <Box sx={{ width: 1, height: resultsHeight, minHeight: 400 }}>
                        <ComponentLoader msg={"Loading"} />
                    </Box>
                )
            )}
        </Box>
    );
};

const sortPayrollRows = (key: PayrollReportKey, rows: any[], lookup: (empId: number | null | undefined) => string) => {
    const byText = (a: string | null | undefined, b: string | null | undefined) => (a ?? "").localeCompare(b ?? "");

    switch (key) {
        case "real":
            return [...rows].sort(
                (a, b) =>
                    byText(a.payPeriodEndDate, b.payPeriodEndDate) ||
                    byText(a.revRecDate, b.revRecDate) ||
                    byText(lookup(a.eventTrainer), lookup(b.eventTrainer))
            );
        case "reconcile":
            return [...rows].sort(
                (a, b) => byText(lookup(a.eventTrainer), lookup(b.eventTrainer)) || byText(a.rateCode, b.rateCode)
            );
        default:
            return rows;
    }
};

export default PayrollReports;
